import {useState, useRef, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {isAndroid, isIOS} from "react-device-detect";
import {addScene, objectAdded, selectObject, unselectObject, deleteObject, changePropertyFromScene} from "store/actions/asset";

import Graphics from "./Graphics";
import "./index.scss";
import {ACTION_TYPE, TRANSFORM_TYPE, OBJECT_CATEGORY, SELFIE, GOOGLE_SHEET, AUTO_SAVE_TIME} from "utils/constants";
import View from "./View";
import {setCodeEditor, setCodeEditorMode} from "store/actions/codeEditor";
import {changeProgress} from "store/actions/progress";
import {changeTransform, turnOnSnapMode, turnOffSnapMode} from "store/actions/transform";
import {initTutorial} from "store/actions/tutorial";
import BumperAd from "./Ad/BumperAd";
import Interstital from "./Ad/Interstital";
import ProgressBox from "./ProgressBox";
import {receiveGameMessage, setGameState} from "store/actions/game";
import {updateSceneInfo} from "store/actions/sceneInfo";
import {changeAdPosition, changeAdShowState, changeAdUsedState} from "store/actions/ad";
import {updatePlaydata} from "store/actions/playData";
import {useGetGoogleSpread} from "hooks/query/GoogleSpread";
import {updateVersionInfo} from "store/actions/version";
import {toggleUndoAble, toggleRedoAble} from "store/actions/undoredo";
import {isMobile} from "react-device-detect";
import {saveProject} from "store/actions/asset";

function Container(props) {
  const canvasRef = useRef();
  const [canvasCtx, setCanvasCtx] = useState(null);

  const [isShowInterstitial, setIsShowInterstitial] = useState(false);
  const [buzzvilTest, setBuzzvilTest] = useState(false); // for buzzvil test
  const [loaded, setLoaded] = useState(true);
  const [percentage, setPercentage] = useState(0);
  const [processName, setProcessName] = useState("");

  const searchParams = new URLSearchParams(window.location.search);
  const {action, selectedSceneName} = useSelector(state => state.asset);
  const transform = useSelector(state => state.transform);
  const mode = useSelector(state => state.mode);
  const helper = useSelector(state => state.helper);
  const grid = useSelector(state => state.grid);
  const undoredo = useSelector(state => state.undoredo);
  const userInfo = useSelector(state => state.userinfo);
  const tutorial = useSelector(state => state.tutorial);
  const selfie = useSelector(state => state.selfie);
  const chatMode = useSelector(state => state.chat.chatMode);
  const chatList = useSelector(state => state.chat.chatList);
  const playOption = useSelector(state => state.playOption);
  const animationBar = useSelector(state => state.animationBar);
  const gameState = useSelector(state => state.game);
  const codeEditor = useSelector(state => state.codeEditor);
  const currentPropertyTab = useSelector(state => state.propertyPanel.currentTab);

  const dispatch = useDispatch();

  const {data: adActiveList} = useGetGoogleSpread(GOOGLE_SHEET.ID_CONTENTS_LIST, GOOGLE_SHEET.VIDEO_AD);
  const isShowBumperAd = adActiveList?.some(item => item.pid === searchParams.get("pid"));
  const isLearnMode = searchParams.get("mode") === "learn";

  const queryParams = new URLSearchParams(window.location.search);
  const runMode = queryParams.get("mode");
  const modeRef = useRef(mode.studioMode);
  useEffect(() => {
    modeRef.current = mode.studioMode;
  }, [mode]);
  const isAutoSaveOn = useSelector(state => state.autoSave);
  const isAutoSaveOnRef = useRef(isAutoSaveOn);
  useEffect(() => {
    isAutoSaveOnRef.current = isAutoSaveOn;
  }, [isAutoSaveOn]);
  const autoSave = () => {
    if (isAutoSaveOnRef.current && modeRef.current === "edit") {
      dispatch(saveProject("auto save"));
    }
  };
  const frameIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const setupAutoSaveLoop = () => {
    const loop = time => {
      if (!startTimeRef.current) {
        startTimeRef.current = time;
      }
      const elapsed = time - startTimeRef.current;

      if (elapsed >= AUTO_SAVE_TIME.time) {
        autoSave();
        startTimeRef.current = time;
      }

      frameIdRef.current = requestAnimationFrame(loop);
    };

    frameIdRef.current = requestAnimationFrame(loop);
  };
  const clearAutoSaveLoop = () => {
    if (frameIdRef.current) {
      cancelAnimationFrame(frameIdRef.current);
      frameIdRef.current = null;
      startTimeRef.current = null;
    }
  };
  useEffect(() => {
    if (mode.studioMode === "edit" && isAutoSaveOn && runMode === "edit") {
      setupAutoSaveLoop();
    } else {
      clearAutoSaveLoop();
    }
    return () => {
      clearAutoSaveLoop();
    };
  }, [mode.studioMode, isAutoSaveOn, runMode]);

  useEffect(() => {
    let ctx = new Graphics(canvasRef.current);
    setCanvasCtx(ctx);
    dispatch(addScene(ctx.sceneGUI));
    dispatch(addScene(ctx.sceneEdit));

    // --- START : ad test ---
    // buzzvil ad test
    if (searchParams.get("pid") === "af250933-7df6-5c7b-9db8-8c4305cd0734") {
      setBuzzvilTest(buzzvilTest + "app test - pid check");
      if (isAndroid || isIOS) {
        setBuzzvilTest(buzzvilTest + "app test - device check");
        setIsShowInterstitial(true);
      }
    }
    // --- END : ad test ---

    ctx.addEventListener("tutorial-initialization", e => {
      dispatch(initTutorial(e.detail));
    });

    ctx.addEventListener(ACTION_TYPE.ADD_SCENE, e => {
      dispatch(addScene(e.detail.object));
    });

    ctx.addEventListener(ACTION_TYPE.OBJECT_ADDED, e => {
      dispatch(objectAdded(e.detail.object));
    });

    ctx.addEventListener(ACTION_TYPE.SELECT_OBJECT, e => {
      dispatch(selectObject(e.detail.object.uuid.substring(0, 3), e.detail.object.uuid, e.detail.target));
    });

    ctx.addEventListener(ACTION_TYPE.UNSELECT_OBJECT, e => {
      dispatch(unselectObject());
    });

    ctx.addEventListener(ACTION_TYPE.SCENE_INFO, e => {
      dispatch(updateSceneInfo(e.detail));
    });

    ctx.addEventListener(ACTION_TYPE.DELETE_OBJECT, e => {
      dispatch(deleteObject());
    });

    ctx.addEventListener(ACTION_TYPE.CHANGE_PROPERTY_FROM_SCENE, e => {
      dispatch(changePropertyFromScene(e.detail.object));
      if (e.detail?.object?.name === "adBannerGui") {
        const size = e.detail.object.userData.adInfo.size;
        const {x, y} = e.detail.object.position;
        dispatch(changeAdPosition(size, x, y));
      }
    });
    ctx.addEventListener(ACTION_TYPE.SET_CODE_EDITOR, e => {
      dispatch(setCodeEditor(false));
    });
    ctx.addEventListener(ACTION_TYPE.SET_CODE_EDITOR_MODE, e => {
      dispatch(setCodeEditorMode(e.detail));
    });
    ctx.addEventListener(ACTION_TYPE.CHANGE_PROGRESS, e => {
      dispatch(changeProgress(e.detail.object));
    });

    ctx.addEventListener(TRANSFORM_TYPE.CHANGE_TRANSFORM, e => {
      dispatch(changeTransform(e.detail));
    });
    ctx.addEventListener(TRANSFORM_TYPE.SNAP_ON, e => {
      dispatch(turnOnSnapMode());
    });
    ctx.addEventListener(TRANSFORM_TYPE.SNAP_OFF, e => {
      dispatch(turnOffSnapMode());
    });

    ctx.addEventListener("LOADING_START", e => {
      setLoaded(false);
      setProcessName(e.detail.object.processName);
    });
    ctx.addEventListener("LOADING_IN_PROGRESS", e => {
      setPercentage(e.detail.object.progress);
      setProcessName(e.detail.object.processName);
    });
    ctx.addEventListener("LOADING_END", e => {
      setLoaded(true);
      dispatch(setGameState("LOADED"));
    });
    ctx.addEventListener(ACTION_TYPE.SET_GAME_STATE, e => {
      dispatch(setGameState(e.detail));
    });
    ctx.addEventListener(ACTION_TYPE.RECEIVE_GAME_MESSAGE, e => {
      dispatch(receiveGameMessage(e.detail));
    });

    ctx.addEventListener(ACTION_TYPE.CHANGE_AD_SHOW_STATE, e => {
      const {size, bool} = e.detail;
      dispatch(changeAdShowState(size, bool));
    });
    ctx.addEventListener(ACTION_TYPE.CHANGE_AD_USED_STATE, e => {
      const {size, bool} = e.detail;
      dispatch(changeAdUsedState(size, bool));
    });
    ctx.addEventListener(ACTION_TYPE.CHANGE_AD_POSITION, e => {
      const {size, x, y} = e.detail;
      dispatch(changeAdPosition(size, x, y));
    });
    ctx.addEventListener("UPDATE_PLAYDATA", e => {
      const {playData} = e.detail;
      dispatch(updatePlaydata(playData));
    });
    ctx.addEventListener(ACTION_TYPE.UPDATE_VERSION_INFO, e => {
      dispatch(updateVersionInfo(e.detail));
    });
    ctx.addEventListener(ACTION_TYPE.TOGGLE_UNDO_ABLE, e => {
      dispatch(toggleUndoAble(e.detail));
    });
    ctx.addEventListener(ACTION_TYPE.TOGGLE_REDO_ABLE, e => {
      dispatch(toggleRedoAble(e.detail));
    });

    return () => {
      ctx.dispose();
    };
  }, []);

  useEffect(() => {
    if (canvasCtx === undefined) return;

    switch (action.category) {
      case OBJECT_CATEGORY.MESH:
        canvasCtx.addMesh(selectedSceneName, action.name);
        break;
      case OBJECT_CATEGORY.LIGHT:
        canvasCtx.addLight(action.name);
        break;
      case OBJECT_CATEGORY.CAMERA:
        canvasCtx.addCamera(action.name);
        break;
      case OBJECT_CATEGORY.STARTLOCATION:
        canvasCtx.addStartLocation(action.object);
        break;
      case OBJECT_CATEGORY.VIDEO:
        canvasCtx.addVideo(action.name, action.object);
        break;
      case OBJECT_CATEGORY.IMAGE:
        canvasCtx.addImage(action.name, action.object);
        break;
      case OBJECT_CATEGORY.DEMO:
        canvasCtx.addDemoModel(action.name, action.object);
        break;
      case OBJECT_CATEGORY.GUI:
        canvasCtx.addGui(action.name, action.thumbnail);
        break;
      case OBJECT_CATEGORY.AD_BANNER:
        canvasCtx.addAdBannerGui(action.name, action.thumbnail, action.adInfo);
        break;
      // case OBJECT_CATEGORY.PARTICLESYSTEM:
      //   canvasCtx.addParticleSystem(action.name);
      //   break;
      case OBJECT_CATEGORY.EFFECT:
        canvasCtx.addParticleSystem(action.object);
        break;
      case OBJECT_CATEGORY.AUDIO:
        canvasCtx.addAudio(action.name, action.object);
        break;
      case OBJECT_CATEGORY.PORTAL:
        canvasCtx.addPortal();
        break;
      default:
        if (process.env.NODE_ENV === "development") {
          console.log("action.category " + action.category + " is not defined category");
        }
        break;
    }

    switch (action.command) {
      case ACTION_TYPE.CHANGE_PROPERTY:
        canvasCtx.updateProperty(action.category, action);
        break;
      case ACTION_TYPE.CHANGE_SCRIPT:
        canvasCtx.updateScript(action.value);
        break;
      case ACTION_TYPE.SAVE_FILE:
        canvasCtx.saveFile("scene");
        break;
      case ACTION_TYPE.LOAD_FILE:
        canvasCtx.loadFile("scene");
        break;
      case ACTION_TYPE.LOAD_GLB:
        canvasCtx.loadGlb();
        break;
      case ACTION_TYPE.LOAD_FROM_PID:
        canvasCtx.loadFromPID(action.targetPID);
        break;
      case ACTION_TYPE.EXPORT_GLB:
        canvasCtx.exportGlb();
        break;
      case ACTION_TYPE.SELECT_OBJECT:
        // TODO: 2D Asset Layer
        if (action.target === "2D") canvasCtx.selectObject(canvasCtx.sceneGUI.getObjectByProperty("uuid", action.name));
        else canvasCtx.selectObject(canvasCtx.sceneEdit.getObjectByProperty("uuid", action.name));
        break;
      case ACTION_TYPE.DELETE_OBJECT:
        if (canvasCtx.selected?.name === "adBannerGui") {
          const size = canvasCtx.selected.userData.adInfo.size;
          dispatch(changeAdUsedState(size, false));
        }
        canvasCtx.removeObject(canvasCtx.selected);
        break;
      case ACTION_TYPE.COPY_OBJECT:
        canvasCtx.copyObject(canvasCtx.selected);
        break;
      case ACTION_TYPE.REORDER_OBJECT:
        canvasCtx.reorderObject(action.value, action.target);
        break;
      case ACTION_TYPE.UPLOAD_FILE:
        canvasCtx.uploadFile({
          name: action.name,
          genre: action.genre,
          description: action.description,
          thumbnail: action.thumbnail,
          isWorldExist: action.isWorldExist,
          group: action.group ? action.group : undefined,
          class: action.class ? action.class : undefined,
          competitionType: action.competitionType,
          phoneNumber: action.phoneNumber,
          agreementDate: action.agreementDate,
        });
        break;
      case ACTION_TYPE.SAVE_PROJECT:
        if (action.name === "auto save") {
          canvasCtx.autoSaveProject();
          return;
        }
        canvasCtx.saveProject();
        break;
      case ACTION_TYPE.ADD_OBJECT:
        break;
      case ACTION_TYPE.CONCEAL_OBJECT:
        if (action.target === "2D") canvasCtx.toggleVisibility(canvasCtx.sceneGUI.getObjectByProperty("uuid", action.name), false);
        else {
          canvasCtx.toggleHelperVisibility(action.name, false);
          canvasCtx.toggleVisibility(canvasCtx.sceneEdit.getObjectByProperty("uuid", action.name), false);
        }
        break;
      case ACTION_TYPE.REVEAL_OBJECT:
        if (action.target === "2D") canvasCtx.toggleVisibility(canvasCtx.sceneGUI.getObjectByProperty("uuid", action.name), true);
        else {
          canvasCtx.toggleHelperVisibility(action.name, true);
          canvasCtx.toggleVisibility(canvasCtx.sceneEdit.getObjectByProperty("uuid", action.name), true);
        }
        break;
      default:
        if (process.env.NODE_ENV === "development") {
          console.log("action.command " + action.command + " is not defined command");
        }
        break;
    }
  }, [action]);

  useEffect(() => {
    canvasCtx && canvasCtx.changeTransform(transform.transformType);
  }, [transform]);

  useEffect(() => {
    canvasCtx && canvasCtx.changeMode(mode.studioMode);
  }, [mode.studioMode]);

  useEffect(() => {
    canvasCtx && canvasCtx.changePublishMode(mode.publishMode);
  }, [mode.publishMode]);

  useEffect(() => {
    canvasCtx && canvasCtx.toggleHelper(helper);
  }, [helper]);

  useEffect(() => {
    canvasCtx && canvasCtx.toggleGrid(grid);
  }, [grid]);

  useEffect(() => {
    canvasCtx && canvasCtx.undoRedo(undoredo);
  }, [undoredo.timestamp]);

  useEffect(() => {
    canvasCtx && canvasCtx.updateUserInfo(userInfo);
  }, [userInfo, canvasCtx]);

  useEffect(() => {
    canvasCtx && canvasCtx.setTutorial(tutorial);
  }, [tutorial.missionIdx, tutorial.completedMissionIdx]);

  useEffect(() => {
    canvasCtx && canvasCtx.getChatMode(chatMode);
  }, [chatMode]);

  useEffect(() => {
    if (chatList.length === 0) return;
    const lastChat = chatList[chatList.length - 1];
    canvasCtx && canvasCtx.makeSpeechBubble(lastChat);
  }, [chatList]);

  useEffect(() => {
    canvasCtx && canvasCtx.getLandMode(playOption);
  }, [playOption]);

  useEffect(() => {
    canvasCtx && canvasCtx.changeSnapMode(transform);
  }, [transform.isActivated]);

  useEffect(() => {
    canvasCtx && canvasCtx.changeSnapMode(transform);
  }, [transform.isActivated]);

  useEffect(() => {
    if (canvasCtx === undefined) return;
    switch (selfie.action.command) {
      case "TAKE_SELFIE":
        canvasCtx.takeSelfie(selfie.type);
        break;
      case "SELFIE_ON":
        canvasCtx.changeShootingMode(SELFIE.SELFIE_MODE);
        break;
      case "SELFIE_OFF":
        canvasCtx.changeShootingMode(SELFIE.NORMAL_MODE);
        break;
      case "STOP_RECORD":
        canvasCtx.stopRecord();
        break;
      case "HIDE_AVATARS":
        canvasCtx.hideAvatars();
        break;
      case "CLOSE_SAVE":
        canvasCtx.closeSaveScreen(selfie.type);
        break;
      case "SWITCH_HAND":
        canvasCtx.switchSelfieHand(selfie.isRightHand);
        break;
      default:
        break;
    }
  }, [selfie.action]);

  useEffect(() => {
    if (!canvasCtx) return;
    canvasCtx.setEmoteAnimation(animationBar.action.name);
  }, [animationBar]);

  useEffect(() => {
    if (!canvasCtx) return;
    canvasCtx.sendMessageToGame(gameState.messageToGame);
  }, [gameState.messageToGame]);

  useEffect(() => {
    canvasCtx && canvasCtx.selectScript(codeEditor.currentScript);
  }, [codeEditor.currentScript]);

  useEffect(() => {
    canvasCtx && currentPropertyTab && (canvasCtx.currentPropertyTab = currentPropertyTab);
  }, [currentPropertyTab]);

  return (
    <>
      <View canvasRef={canvasRef} isLearnMode={isLearnMode} />
      {!isMobile && isShowBumperAd && <BumperAd />}
      {isShowInterstitial && <Interstital buzzvilTest={buzzvilTest} setBuzzvilTest={setBuzzvilTest} />}
      {buzzvilTest && (
        <div
          style={{
            position: "absolute",
            top: "10%",
            left: "50%",
            transform: "translateX(-50%)",
            color: "#000",
            textAlign: "center",
            border: "1px solid red",
            maxHeight: "50vh",
            overflowY: "auto",
          }}
        >
          {buzzvilTest}
        </div>
      )}
      {!props.isIPXMode && <ProgressBox percentage={percentage} loaded={loaded} processName={processName} />}
    </>
  );
}

export default Container;
